import { useState, useEffect, useCallback } from "react";
import { useProfile } from "../../../hooks/useProfile";
import {
  fetchEmployeeCountAPI,
  fetchEmployeeCountsAPI,
  fetchEmployeesAPI,
  fetchArchivedEmployeesAPI,
  fetchCompaniesAPI,
  fetchClientsAPI,
} from "../services/employeeService";
import {
  mapEmployeesData,
  mapArchivedEmployeesData,
  countNewEmployees,
} from "../utils/employeeFormatters";

/**
 * 🪝 useEmployees Hook
 * Hook بسيط يجمع البيانات من service و formatters
 */
export const useEmployees = () => {
  const { profile } = useProfile();
  const clientName = profile?.clientName ?? null;
  const [employees, setEmployees] = useState([]);
  const [archivedEmployees, setArchivedEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [clientsMap, setClientsMap] = useState({});
  const [clientsList, setClientsList] = useState([]);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [archivedCount, setArchivedCount] = useState(0);
  const [counts, setCounts] = useState({
    Active: 0,
    OnLeave: 0,
    Recent: 0,
    archived: 0,
    newEmployees: 0,
    Total: 0,
  });
  // Starts true (not false) so the page-level skeleton is guaranteed to be
  // visible on first paint instead of racing the mount effect that flips it.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const viewType = (localStorage.getItem('managerViewType') || localStorage.getItem('viewType') || 'manager').toLowerCase();

  // ============== FETCH ALL DATA ==============
  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      // Always fetch employee data first so the table can render even if counts or clients fail.
      const employeesData = await fetchEmployeesAPI(viewType, clientName);
      setEmployees(mapEmployeesData(employeesData));

      // Fetch supporting data in parallel, but treat failures as non-fatal.
      const countPromise = fetchEmployeeCountAPI(clientName).catch((err) => {
        console.warn("Failed to fetch employee count:", err);
        return 0;
      });
      const countsPromise = fetchEmployeeCountsAPI(viewType, clientName).catch((err) => {
        console.warn("Failed to fetch employee counts:", err);
        return {
          Active: 0,
          OnLeave: 0,
          Recent: 0,
          archived: 0,
          newEmployees: 0,
          Total: 0,
        };
      });
      const archivedPromise = fetchArchivedEmployeesAPI(clientName).catch((err) => {
        console.warn("Failed to fetch archived employees:", err);
        return [];
      });
      const companiesPromise = fetchCompaniesAPI(viewType, clientName).catch((err) => {
        console.warn("Failed to fetch companies:", err);
        return [];
      });
      const clientsPromise = fetchClientsAPI().catch((err) => {
        console.warn("Failed to fetch clients:", err);
        return { map: {}, list: [] };
      });

      const [
        count,
        countsData,
        archivedData,
        companiesData,
        clientsData,
      ] = await Promise.all([
        countPromise,
        countsPromise,
        archivedPromise,
        companiesPromise,
        clientsPromise,
      ]);

      setEmployeeCount(count);
      setCounts(countsData);
      setArchivedEmployees(mapArchivedEmployeesData(archivedData));
      setArchivedCount(archivedData.length);
      setCompanies(companiesData);
      setClientsMap(clientsData.map);
      setClientsList(clientsData.list);
    } catch (err) {
      console.error("❌ Error fetching employees:", err);
      setError("Failed to fetch employees data");
    } finally {
      setLoading(false);
    }
  }, [clientName, viewType]);

  // ============== UPDATE COUNTS BASED ON EMPLOYEES ==============
  useEffect(() => {
    // Always compute the new employees
    const newEmployeesCount = countNewEmployees(employees);

    // Calculate active and on leave from the currently visible employees
    let activeCount = 0;
    let onLeaveCount = 0;
    
    employees.forEach(emp => {
      const status = (emp.status || "").toLowerCase().replace(/\s+/g, "");
      if (status === "active") activeCount++;
      else if (status === "onleave") onLeaveCount++;
    });

    const isAdminView = viewType === "admin";
    
    setCounts((prev) => {
      // For managers (!isAdminView), we MUST calculate from visibility (the `employees` array)
      // otherwise they see company-wide stats from the backend count endpoints.
      const useVisible = !isAdminView || isAdminView; // Always use visible for accurate filtered stats
      
      const Active = useVisible ? activeCount : (prev.Active || 0);
      const OnLeave = useVisible ? onLeaveCount : (prev.OnLeave || 0);
      const Total = useVisible ? employees.length : ((prev.Active || 0) + (prev.OnLeave || 0) + (archivedCount || 0));

      return {
        ...prev,
        Active,
        OnLeave,
        archived: archivedCount || 0,
        newEmployees: newEmployeesCount,
        Total,
      };
    });

    if (isAdminView) {
      setEmployeeCount(employees.length);
    } else {
      // If not admin view but counts are somehow zero, we use visible employees.length.
      // But we can just use `setEmployeeCount(prev => prev || employees.length)` as a simple fallback
      setEmployeeCount(prev => prev || employees.length);
    }
  }, [archivedCount, employees, viewType]);

  // ============== INITIAL FETCH ON MOUNT ==============
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleEmployeeDataUpdated = () => {
      fetchAll();
    };

    window.addEventListener("employee-data-updated", handleEmployeeDataUpdated);
    return () => {
      window.removeEventListener("employee-data-updated", handleEmployeeDataUpdated);
    };
  }, [fetchAll]);


  // ============== RETURN PUBLIC API ==============
  return {
    // Data
    employees,
    setEmployees,
    archivedEmployees,
    setArchivedEmployees,
    companies,
    clientsMap,
    clientsList,
    employeeCount,
    archivedCount,
    counts,
    selectedEmployee,
    
    // Setters
    setSelectedEmployee,
    setCounts,
    setEmployeeCount,
    setArchivedCount,
    
    // State
    loading,
    setLoading,
    error,
    setError,
    
    // Methods
    fetchAll,
  };
};
